import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiUsers, FiClock, FiHeart } = FiIcons;

const About = () => {
  const stats = [
    { number: '15+', label: 'Years Experience', icon: FiClock },
    { number: '10,000+', label: 'Happy Customers', icon: FiUsers },
    { number: '50,000+', label: 'Items Cleaned', icon: FiAward },
    { number: '99%', label: 'Satisfaction Rate', icon: FiHeart }
  ];

  const team = [
    {
      name: 'John Smith',
      position: 'Founder & CEO',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face',
      description: 'With 20+ years in the laundry industry, John founded Fresh & Clean with a vision to provide premium laundry services.'
    },
    {
      name: 'Sarah Johnson',
      position: 'Operations Manager',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face',
      description: 'Sarah ensures smooth daily operations and maintains our high standards of quality and customer service.'
    },
    {
      name: 'Mike Chen',
      position: 'Quality Control Supervisor',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face',
      description: 'Mike oversees quality control processes and ensures every garment meets our premium standards.'
    }
  ];

  const values = [
    {
      title: 'Quality First',
      description: 'We never compromise on quality. Every garment receives meticulous care and attention.',
      icon: FiAward
    },
    {
      title: 'Customer Focus',
      description: 'Your satisfaction is our priority. We go above and beyond to exceed expectations.',
      icon: FiHeart
    },
    {
      title: 'Reliability',
      description: 'Consistent, dependable service you can count on, every single time.',
      icon: FiClock
    },
    {
      title: 'Community',
      description: 'We are proud to serve our local community and build lasting relationships.',
      icon: FiUsers
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            About Fresh & Clean
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your trusted partner for professional laundry and dry cleaning services since 2009. 
            We combine traditional care with modern convenience.
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-blue-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <SafeIcon icon={stat.icon} className="w-12 h-12 mx-auto mb-4" />
                <div className="text-3xl md:text-4xl font-bold mb-2">{stat.number}</div>
                <div className="text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Our Story Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">Our Story</h2>
            <div className="space-y-4 text-gray-600">
              <p>
                Fresh & Clean Laundry was founded in 2009 with a simple mission: to provide 
                exceptional laundry and dry cleaning services that busy families and professionals 
                can rely on. What started as a small neighborhood laundromat has grown into the 
                area's most trusted laundry service provider.
              </p>
              <p>
                Our founder, John Smith, recognized the need for high-quality, convenient laundry 
                services in our community. With his background in textile care and business 
                management, he set out to create a service that would set new standards for 
                quality and customer satisfaction.
              </p>
              <p>
                Today, we serve thousands of satisfied customers throughout the region, offering 
                everything from basic wash and fold to specialized dry cleaning and alterations. 
                Our commitment to quality, convenience, and customer service remains as strong 
                as ever.
              </p>
            </div>
          </div>
          <div>
            <img 
              src="https://images.unsplash.com/photo-1517677208171-0bc6725a3e60?w=600&h=400&fit=crop" 
              alt="Our laundry facility"
              className="rounded-lg shadow-lg w-full"
            />
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do and every service we provide
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Meet Our Team</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            The dedicated professionals who make Fresh & Clean the best choice for your laundry needs
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {team.map((member, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6 text-center">
              <img 
                src={member.image} 
                alt={member.name}
                className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
              />
              <h3 className="text-xl font-semibold text-gray-800 mb-1">{member.name}</h3>
              <p className="text-blue-600 font-medium mb-3">{member.position}</p>
              <p className="text-gray-600">{member.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Mission Section */}
      <div className="bg-gray-100 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">Our Mission</h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-gray-600 mb-8">
              "To provide exceptional laundry and dry cleaning services that save our customers time 
              while delivering superior results. We are committed to treating every garment with the 
              care it deserves and every customer with the respect they expect."
            </p>
            <Link
              to="/contact"
              className="bg-blue-600 text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
            >
              Experience Our Service
            </Link>
          </div>
        </div>
      </div>

      {/* Certifications Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Certifications & Awards</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Recognition for our commitment to quality and environmental responsibility
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="p-6">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiAward} className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Eco-Friendly Certified</h3>
            <p className="text-gray-600">Certified for using environmentally safe cleaning processes</p>
          </div>
          
          <div className="p-6">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiAward} className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Best Laundry Service 2023</h3>
            <p className="text-gray-600">Awarded by Local Business Association</p>
          </div>
          
          <div className="p-6">
            <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiAward} className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Customer Choice Award</h3>
            <p className="text-gray-600">Highest customer satisfaction rating for 3 consecutive years</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;